<div class="user-profile-container settings-page">

  <div class="settings-page__content">
    <p class="settings-page__description">
      {{ 'SETTINGS.USER_PROFILE.DESCRIPTION' | translate }}
    </p>

    @if (initialLoadFailed() && loadError(); as error) {
      <tf-settings-load-error
        [title]="error"
        [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
        [details]="loadErrorDetails()"
        [retrying]="isLoading()"
        (retry)="retryLoad()" />
    } @else if (!hasLoadedOnce()) {
      <div class="user-profile-loading-skeleton" aria-hidden="true">
        <section class="user-profile-loading-skeleton__card">
          <span
            class="user-profile-loading-skeleton__bar user-profile-loading-skeleton__bar--title"></span>
          <span
            class="user-profile-loading-skeleton__bar user-profile-loading-skeleton__bar--text"></span>
          <span class="user-profile-loading-skeleton__control"></span>
          <div class="user-profile-loading-skeleton__grid">
            <span class="user-profile-loading-skeleton__control"></span>
            <span class="user-profile-loading-skeleton__control"></span>
          </div>
          <span class="user-profile-loading-skeleton__control"></span>
        </section>

        <section class="user-profile-loading-skeleton__card">
          <span
            class="user-profile-loading-skeleton__bar user-profile-loading-skeleton__bar--title"></span>
          <span
            class="user-profile-loading-skeleton__bar user-profile-loading-skeleton__bar--text"></span>
          <span class="user-profile-loading-skeleton__control"></span>
          <span class="user-profile-loading-skeleton__control"></span>
          <span
            class="user-profile-loading-skeleton__bar user-profile-loading-skeleton__bar--text-short"></span>
          <span class="user-profile-loading-skeleton__control"></span>
          <span class="user-profile-loading-skeleton__button"></span>
        </section>
      </div>
    } @else {
      <div class="user-profile-cards">
        <!-- Profile section -->
        <section class="user-profile-card" aria-labelledby="user-profile-section-heading">
          <tf-settings-inline-message
            [message]="profileError()"
            messageId="user-profile-profile-error"
            (dismissed)="clearProfileError()" />

          <div class="user-profile-card__header">
            <h3 id="user-profile-section-heading" class="settings-page__section-title">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.HEADING' | translate }}
            </h3>
            <p class="settings-page__description">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.DESCRIPTION' | translate }}
            </p>
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-email" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.EMAIL' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-email"
                type="email"
                [value]="currentUserEmail()"
                readonly
                disabled
                class="w-full" />
            </div>
            <small class="settings-page__hint">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.EMAIL_HINT' | translate }}
            </small>
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-firstname" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.FIRSTNAME' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-firstname"
                type="text"
                autocomplete="given-name"
                [formField]="profileForm.firstname"
                [class.tf-show-invalid]="profileSubmitted() || profileForm.firstname().touched()"
                [attr.aria-invalid]="
                  (profileSubmitted() && profileForm.firstname().invalid()) ||
                  !!profileServerErrors.value()['firstname']
                    ? true
                    : null
                "
                [attr.aria-describedby]="
                  profileServerErrors.value()['firstname']
                    ? 'user-profile-firstname-error'
                    : profileSubmitted() && profileForm.firstname().invalid()
                      ? 'user-profile-firstname-validation'
                      : null
                "
                class="w-full" />
            </div>
            @if (profileSubmitted() && profileForm.firstname().invalid()) {
              <small id="user-profile-firstname-validation" class="p-error">
                {{ profileForm.firstname().errors()[0]?.message ?? '' | translate }}
              </small>
            }
            @if (profileServerErrors.value()['firstname']; as error) {
              <small id="user-profile-firstname-error" class="p-error" role="alert">
                {{ error }}
              </small>
            }
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-lastname" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.LASTNAME' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-lastname"
                type="text"
                autocomplete="family-name"
                [formField]="profileForm.lastname"
                [class.tf-show-invalid]="profileSubmitted() || profileForm.lastname().touched()"
                [attr.aria-invalid]="
                  (profileSubmitted() && profileForm.lastname().invalid()) ||
                  !!profileServerErrors.value()['lastname']
                    ? true
                    : null
                "
                [attr.aria-describedby]="
                  profileServerErrors.value()['lastname']
                    ? 'user-profile-lastname-error'
                    : profileSubmitted() && profileForm.lastname().invalid()
                      ? 'user-profile-lastname-validation'
                      : null
                "
                class="w-full" />
            </div>
            @if (profileSubmitted() && profileForm.lastname().invalid()) {
              <small id="user-profile-lastname-validation" class="p-error">
                {{ profileForm.lastname().errors()[0]?.message ?? '' | translate }}
              </small>
            }
            @if (profileServerErrors.value()['lastname']; as error) {
              <small id="user-profile-lastname-error" class="p-error" role="alert">
                {{ error }}
              </small>
            }
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-phone" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PROFILE.PHONE_NUMBER' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <tf-phone-number
                inputId="user-profile-phone"
                [ngModel]="profileModel().phoneNumber"
                (ngModelChange)="onPhoneNumberChange($event)"
                (focusout)="onPhoneNumberBlur()"
                [invalid]="showPhoneClientError() || !!profileServerErrors.value()['phoneNumber']"
                [ariaDescribedBy]="
                  profileServerErrors.value()['phoneNumber']
                    ? 'user-profile-phone-error'
                    : showPhoneClientError()
                      ? 'user-profile-phone-validation'
                      : undefined
                " />
            </div>
            @if (showPhoneClientError()) {
              <small id="user-profile-phone-validation" class="p-error" role="alert">
                {{ 'SETTINGS.USER_PROFILE.VALIDATION.PHONE_INVALID' | translate }}
              </small>
            }
            @if (profileServerErrors.value()['phoneNumber']; as error) {
              <small id="user-profile-phone-error" class="p-error" role="alert">{{ error }}</small>
            }
          </div>
        </section>

        <section
          class="user-profile-card password-section"
          aria-labelledby="user-profile-password-heading">
          <tf-settings-inline-message
            [message]="passwordError()"
            messageId="user-profile-password-error"
            (dismissed)="clearPasswordError()" />

          <div class="user-profile-card__header">
            <h3 id="user-profile-password-heading" class="settings-page__section-title">
              {{ 'SETTINGS.USER_PROFILE.PASSWORD.HEADING' | translate }}
            </h3>
            <p class="settings-page__description">
              {{ 'SETTINGS.USER_PROFILE.PASSWORD.DESCRIPTION' | translate }}
            </p>
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-current-password" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PASSWORD.CURRENT' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-current-password"
                type="password"
                autocomplete="current-password"
                [formField]="passwordForm.current"
                [class.tf-show-invalid]="passwordSubmitted() || passwordForm.current().touched()"
                [attr.aria-invalid]="
                  (passwordSubmitted() && passwordForm.current().invalid()) ||
                  !!passwordServerErrors.value()['current']
                    ? true
                    : null
                "
                [attr.aria-describedby]="
                  passwordServerErrors.value()['current']
                    ? 'user-profile-current-password-error'
                    : passwordSubmitted() && passwordForm.current().invalid()
                      ? 'user-profile-current-password-validation'
                      : null
                "
                class="w-full" />
            </div>
            @if (passwordSubmitted() && passwordForm.current().invalid()) {
              <small id="user-profile-current-password-validation" class="p-error">
                {{ passwordForm.current().errors()[0]?.message ?? '' | translate }}
              </small>
            }
            @if (passwordServerErrors.value()['current']; as error) {
              <small id="user-profile-current-password-error" class="p-error" role="alert">
                {{ error }}
              </small>
            }
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-new-password" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PASSWORD.NEW' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-new-password"
                type="password"
                autocomplete="new-password"
                [formField]="passwordForm.next"
                [class.tf-show-invalid]="passwordSubmitted() || passwordForm.next().touched()"
                [attr.aria-invalid]="passwordServerErrors.value()['next'] ? true : null"
                [attr.aria-describedby]="
                  passwordServerErrors.value()['next'] ? 'user-profile-new-password-error' : null
                "
                class="w-full" />
            </div>
            @if (passwordServerErrors.value()['next']; as error) {
              <small id="user-profile-new-password-error" class="p-error" role="alert">
                {{ error }}
              </small>
            }
            <ul
              class="password-policy"
              aria-label="{{ 'SETTINGS.USER_PROFILE.PASSWORD.RULES' | translate }}">
              <li [class.password-policy__item--ok]="hasMinLength()">
                <i
                  class="pi"
                  [class.pi-check]="hasMinLength()"
                  [class.pi-circle]="!hasMinLength()"
                  aria-hidden="true"></i>
                {{ 'SETTINGS.USER_PROFILE.PASSWORD.RULE_MIN_LENGTH' | translate }}
              </li>
              <li [class.password-policy__item--ok]="hasUpperCase()">
                <i
                  class="pi"
                  [class.pi-check]="hasUpperCase()"
                  [class.pi-circle]="!hasUpperCase()"
                  aria-hidden="true"></i>
                {{ 'SETTINGS.USER_PROFILE.PASSWORD.RULE_UPPERCASE' | translate }}
              </li>
              <li [class.password-policy__item--ok]="hasLowerCase()">
                <i
                  class="pi"
                  [class.pi-check]="hasLowerCase()"
                  [class.pi-circle]="!hasLowerCase()"
                  aria-hidden="true"></i>
                {{ 'SETTINGS.USER_PROFILE.PASSWORD.RULE_LOWERCASE' | translate }}
              </li>
              <li [class.password-policy__item--ok]="hasNumber()">
                <i
                  class="pi"
                  [class.pi-check]="hasNumber()"
                  [class.pi-circle]="!hasNumber()"
                  aria-hidden="true"></i>
                {{ 'SETTINGS.USER_PROFILE.PASSWORD.RULE_NUMBER' | translate }}
              </li>
            </ul>
          </div>

          <div class="form-group settings-page__field">
            <label for="user-profile-confirm-password" class="settings-page__label">
              {{ 'SETTINGS.USER_PROFILE.PASSWORD.CONFIRM' | translate }}
            </label>
            <div class="settings-page__field-input-wrap">
              <input
                pInputText
                id="user-profile-confirm-password"
                type="password"
                autocomplete="new-password"
                [formField]="passwordForm.confirm"
                [class.tf-show-invalid]="passwordSubmitted() || passwordForm.confirm().touched()"
                [attr.aria-invalid]="
                  passwordModel().confirm !== '' && !passwordsMatch() ? true : null
                "
                [attr.aria-describedby]="
                  passwordModel().confirm !== '' && !passwordsMatch()
                    ? 'user-profile-confirm-password-error'
                    : null
                "
                class="w-full" />
            </div>
            @if (passwordModel().confirm !== '' && !passwordsMatch()) {
              <small id="user-profile-confirm-password-error" class="p-error">
                {{ 'SETTINGS.USER_PROFILE.PASSWORD.RULE_MATCH' | translate }}
              </small>
            }
          </div>

          <div class="user-profile-card__actions">
            <p-button
              [label]="'SETTINGS.USER_PROFILE.PASSWORD.CHANGE' | translate"
              [loading]="isSavingPassword()"
              [disabled]="!passwordPolicyOk() || isSavingPassword()"
              (onClick)="savePassword()" />
          </div>
        </section>
      </div>
    }
  </div>

  @if (hasLoadedOnce() && !initialLoadFailed()) {
    <tf-settings-actions
      (cancel)="cancelProfile()"
      (save)="saveProfile()"
      [disabled]="
        profileForm().invalid() || phoneNumberInvalid() || isLoading() || isSavingProfile()
      "
      [loading]="isSavingProfile()" />
  }
</div>
